import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ShoppingCart, ShoppingBag, Info, Sparkles } from 'lucide-react';
import api from '../services/api';
import ContactButton from '../components/ContactButton';
import { useCart } from '../context/CartContext';
import HookItUpButton from '../components/HookItUpButton';
import CartModal from '../components/CartModal';
import VirtualTryOn from '../components/VirtualTryOn';

const ProductDetail = () => {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { addToCart, goToCheckout, cart, loading: cartLoading } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [payDeposit, setPayDeposit] = useState(false);
  const [hookItUpInfoOpen, setHookItUpInfoOpen] = useState(false);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [virtualTryOnOpen, setVirtualTryOnOpen] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [handle]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/shopify/products/${handle}`);
      if (response.data.success) {
        const productData = response.data.product;
        setProduct(productData);
        // Set first variant as default
        if (productData.variants && productData.variants.length > 0) {
          setSelectedVariant(productData.variants[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = async () => {
    if (!selectedVariant) {
      toast.error('Please select a variant');
      return;
    }

    try {
      setPurchaseLoading(true);
      
      // If deposit option is selected, handle it differently
      if (payDeposit) {
        const depositData = {
          product_id: product.id,
          product_name: product.name,
          product_handle: product.handle,
          variant_id: selectedVariant.id,
          variant_title: selectedVariant.title,
          full_price: selectedVariant.price,
          deposit_amount: selectedVariant.price * 0.5,
          balance_amount: selectedVariant.price * 0.5
        };
        
        const response = await api.post('/deposits/create-checkout', depositData);
        
        if (response.data.success && response.data.checkout_url) {
          // Store deposit info in localStorage for post-checkout processing
          localStorage.setItem('pending_deposit', JSON.stringify({
            ...depositData,
            checkout_id: response.data.checkout_id
          }));
          window.location.href = response.data.checkout_url;
        } else {
          toast.error('Failed to create deposit checkout');
        }
      } else {
        // Normal checkout
        const checkoutData = {
          lineItems: [
            {
              variantId: selectedVariant.id,
              quantity: 1
            }
          ]
        };

        const response = await api.post('/shopify/checkout', checkoutData);
        
        if (response.data.success && response.data.checkout.webUrl) {
          window.location.href = response.data.checkout.webUrl;
        } else {
          toast.error('Failed to create checkout');
        }
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to process checkout. Please try again.');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) {
      toast.error('Please select a variant');
      return;
    }

    const success = await addToCart(
      selectedVariant.id,
      1,
      `${product.name} - ${selectedVariant.title}`
    );

    if (success) {
      // Optional: Show cart preview or keep shopping
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-charcoal text-2xl font-bold">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center">
        <div className="text-charcoal text-2xl font-bold mb-4">Product not found</div>
        <Button onClick={() => navigate('/')}>Back to Home</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-charcoal/95 backdrop-blur-md border-b border-warmSage/20">
        <nav className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/bcsdxd12_IMG_2797.PNG" 
                alt="PRSVR Logo" 
                className="h-10"
                style={{ filter: 'brightness(0) saturate(100%) invert(98%) sepia(6%) saturate(214%) hue-rotate(327deg) brightness(102%) contrast(96%)' }}
              />
            </a>
            <div className="flex items-center gap-2 sm:gap-4">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-cream hover:text-warmSage text-sm sm:text-base px-2 sm:px-4">
                Back
              </Button>
              <Button 
                onClick={() => setCartModalOpen(true)}
                className="bg-warmSage text-charcoal hover:bg-warmSage/90 font-bold relative"
              >
                <ShoppingCart className="h-5 w-5" />
                {cart && cart.lineItems && cart.lineItems.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-charcoal text-cream w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold">
                    {cart.lineItems.reduce((total, item) => total + item.quantity, 0)}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 pt-24 sm:pt-32 pb-32 max-w-full overflow-x-hidden">
        <div className="grid md:grid-cols-2 gap-6 md:gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="aspect-[4/5] bg-warmGray/10 rounded-lg overflow-hidden border-4 border-charcoal">
              {product.images && product.images.length > 0 ? (
                <img 
                  src={product.images[selectedImage].url} 
                  alt={product.images[selectedImage].alt || product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-warmGray">
                  No image available
                </div>
              )}
            </div>
            
            {/* Thumbnail Gallery */}
            {product.images && product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-2 sm:gap-4">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-[4/5] rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index ? 'border-warmSage' : 'border-charcoal hover:border-warmSage/50'
                    }`}
                  >
                    <img 
                      src={image.url} 
                      alt={image.alt || `${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6 overflow-hidden">
            <div>
              {product.tags && product.tags.includes('made-to-order') ? (
                <div>
                  <p 
                    className="text-blue-600 text-sm font-bold uppercase tracking-wider mb-2 bg-blue-100 inline-block px-3 py-2 rounded cursor-pointer hover:bg-blue-200 transition-all border-2 border-blue-600"
                    onClick={() => navigate('/pricing-policy')}
                    title="Click to learn more about our Made to Order process"
                  >
                    🔨 MADE TO ORDER
                  </p>
                  <p className="text-xs text-warmGray mt-2 italic">
                    Crafted specifically for you. Delivery: up to 3 weeks. 
                    <span 
                      className="underline cursor-pointer hover:text-warmSage ml-1"
                      onClick={() => navigate('/pricing-policy')}
                    >
                      Learn more
                    </span>
                  </p>
                </div>
              ) : product.category ? (
                <p className="text-warmSage text-sm font-bold uppercase tracking-wider mb-2">
                  {product.category}
                </p>
              ) : null}
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-charcoal uppercase mb-4 break-words">
                {product.name}
              </h1>
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-charcoal">
                  ${selectedVariant ? (payDeposit ? (selectedVariant.price * 0.5).toFixed(2) : selectedVariant.price.toFixed(2)) : '0.00'}
                </p>
                {payDeposit && selectedVariant && (
                  <p className="text-sm text-warmGray mt-1">
                    50% Deposit • Balance: ${(selectedVariant.price * 0.5).toFixed(2)} due upon completion
                  </p>
                )}
              </div>
            </div>

            {/* Make A Deposit Detailed Tile - Only for Made to Order */}
            {product.tags && product.tags.includes('made-to-order') && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={payDeposit}
                    onChange={(e) => setPayDeposit(e.target.checked)}
                    className="mt-1 h-5 w-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-charcoal flex items-center gap-2 mb-2">
                      <span className="text-lg">Make A Deposit</span>
                    </div>
                    <p className="text-sm text-warmGray mb-3">
                      Reserve with a 50% deposit. Invoice for balance when ready to ship.
                    </p>
                    {payDeposit && selectedVariant && (
                      <div className="p-3 bg-white rounded border border-blue-200">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-warmGray">Deposit Today:</span>
                          <span className="font-bold text-blue-600">${(selectedVariant.price * 0.5).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-warmGray">Balance Later:</span>
                          <span className="font-bold text-charcoal">${(selectedVariant.price * 0.5).toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            )}

            {/* Description */}
            <div className="prose prose-lg max-w-full">
              <p className="text-warmGray leading-relaxed break-words">
                {product.description}
              </p>
            </div>

            {/* Variant Selection */}
            {product.variants && product.variants.length > 1 && (
              <div className="space-y-3">
                <label className="text-sm font-bold text-charcoal uppercase tracking-wider">
                  Select Variant
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {product.variants.map((variant) => (
                    <button
                      key={variant.id}
                      onClick={() => setSelectedVariant(variant)}
                      disabled={!variant.available}
                      className={`p-3 sm:p-4 rounded-lg border-2 font-bold text-sm transition-all truncate ${
                        selectedVariant?.id === variant.id
                          ? 'border-warmSage bg-cream text-charcoal'
                          : variant.available
                          ? 'border-charcoal text-charcoal hover:border-warmSage'
                          : 'border-warmGray/30 text-warmGray cursor-not-allowed'
                      }`}
                    >
                      {variant.title}
                      {!variant.available && (
                        <span className="block text-xs mt-1">Out of Stock</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Availability */}
            <div className="flex items-center gap-2 mb-6">
              {product.tags && product.tags.includes('made-to-order') ? (
                <>
                  <div className="w-3 h-3 rounded-full bg-blue-600" />
                  <span className="text-sm font-bold text-blue-600">
                    Made to Order
                  </span>
                </>
              ) : (
                <>
                  <div className={`w-3 h-3 rounded-full ${selectedVariant?.available ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-sm font-bold text-charcoal">
                    {selectedVariant?.available ? 'In Stock' : 'Out of Stock'}
                  </span>
                </>
              )}
            </div>

            {/* Customize Button for customizable products */}
            {(handle.includes('valley') || handle.includes('collegiate')) && (
              <Button
                onClick={() => navigate(`/customize/${handle}`)}
                className="w-full bg-purple-600 text-white hover:bg-purple-700 font-black text-base sm:text-lg py-4 sm:py-6 uppercase tracking-wider mb-4"
              >
                🎨 CUSTOMIZE
              </Button>
            )}

            {/* Add to Cart and Buy Now Buttons */}
            <div className="space-y-3">
              <div>
                <div className="flex flex-col sm:grid sm:grid-cols-2 gap-3">
                  <Button 
                    onClick={handleAddToCart}
                    disabled={!selectedVariant?.available || cartLoading}
                    className="bg-warmSage text-charcoal hover:bg-warmSage/90 font-black text-sm sm:text-base py-4 sm:py-6 px-3 uppercase tracking-wide border-4 border-charcoal transition-all w-full"
                  >
                    <ShoppingCart className="mr-1.5 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                    <span className="truncate">{cartLoading ? 'Adding...' : 'Add to Cart'}</span>
                  </Button>
                  
                  <HookItUpButton product={product} variant={selectedVariant} />
                </div>

                {/* Small links row - Each under their respective button */}
                <div className="flex flex-col sm:grid sm:grid-cols-2 gap-3 mt-3">
                  {/* Left: Make A Deposit under Add to Cart - Only for Made to Order */}
                  <div>
                    {product.tags && product.tags.includes('made-to-order') && (
                      <label className="flex items-center gap-1.5 cursor-pointer text-sm text-charcoal hover:text-blue-600 transition-colors">
                        <input
                          type="checkbox"
                          checked={payDeposit}
                          onChange={(e) => setPayDeposit(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>Make A Deposit</span>
                      </label>
                    )}
                  </div>
                  
                  {/* Right: What's Hook It Up under Hook It Up button */}
                  <div>
                    <button
                      onClick={() => setHookItUpInfoOpen(true)}
                      className="text-sm text-blue-600 hover:text-blue-700 underline flex items-center gap-1"
                    >
                      <Info className="h-4 w-4" />
                      What's "Hook It Up"?
                    </button>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={handleBuyNow}
                disabled={!selectedVariant?.available || purchaseLoading}
                className="w-full bg-charcoal text-cream hover:bg-charcoal/90 font-black text-base sm:text-lg py-4 sm:py-6 uppercase tracking-wider border-4 border-charcoal transition-all"
              >
                <ShoppingBag className="mr-2 h-5 w-5 sm:h-6 sm:w-6" />
                {purchaseLoading ? 'Processing...' : 'Buy Now'}
              </Button>

              {/* Virtual Try-On Button */}
              <Button 
                onClick={() => setVirtualTryOnOpen(true)}
                variant="outline"
                className="w-full border-2 border-warmSage text-warmSage hover:bg-warmSage hover:text-black font-bold text-base py-5 uppercase tracking-wide transition-all"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Virtual Fitting & Scenario Styling
              </Button>
            </div>

            {/* Product Details */}
            <div className="border-t-2 border-charcoal pt-6 space-y-4">
              <h3 className="text-xl font-black text-charcoal uppercase">Product Details</h3>
              <ul className="space-y-2 text-warmGray">
                <li>• Premium quality materials</li>
                <li>• Designed in Los Angeles</li>
                <li>• Part of the PRSVR collection</li>
                <li>• Limited availability</li>
              </ul>
            </div>

            {/* Contact */}
            <div className="bg-charcoal text-cream p-6 rounded-lg">
              <h4 className="font-bold mb-2">Need Help?</h4>
              <p className="text-sm mb-3">Contact us for sizing or product questions</p>
              <Button 
                onClick={() => window.location.href = 'mailto:info@prsvr.com'}
                variant="outline"
                className="w-full border-cream text-cream hover:bg-cream hover:text-charcoal"
              >
                Email Us
              </Button>
            </div>
          </div>
        </div>

        {/* Recommended Products - Only show for Taylor Suit */}
        {product.handle === 'the-taylor-suit-2-piece-set' && (
          <div className="mt-20">
            <h2 className="text-4xl font-black text-charcoal uppercase mb-8 text-center">
              You May Also Like
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {/* The Taylor Jacket */}
              <div 
                className="group cursor-pointer"
                onClick={() => window.location.href = '/products/the-taylor-jacket'}
              >
                <div className="relative overflow-hidden aspect-[4/5] border-4 border-charcoal rounded-lg mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/aeoxnjzq_IMG_2796.JPG" 
                    alt="The Taylor Jacket"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/20 transition-all duration-300 flex items-center justify-center">
                    <Button className="opacity-0 group-hover:opacity-100 transition-opacity bg-warmSage text-charcoal hover:bg-warmSage/90 font-bold">
                      VIEW PRODUCT
                    </Button>
                  </div>
                </div>
                <div className="bg-charcoal text-cream p-4 rounded-lg">
                  <h3 className="text-xl font-bold uppercase mb-2">The Taylor Jacket</h3>
                  <p className="text-warmGray text-sm mb-3">Premium outerwear. Pairs perfectly with the suit.</p>
                  <p className="text-2xl font-black text-warmSage">$175.00</p>
                </div>
              </div>

              {/* The Taylor Suit - Youth */}
              <div 
                className="group cursor-pointer"
                onClick={() => window.location.href = '/products/the-taylor-suit-youth'}
              >
                <div className="relative overflow-hidden aspect-[4/5] border-4 border-charcoal rounded-lg mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_prsvr-uniforms/artifacts/i3qamr5f_IMG_3259.jpeg" 
                    alt="The Taylor Suit - Youth"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/20 transition-all duration-300 flex items-center justify-center">
                    <Button className="opacity-0 group-hover:opacity-100 transition-opacity bg-warmSage text-charcoal hover:bg-warmSage/90 font-bold">
                      VIEW PRODUCT
                    </Button>
                  </div>
                </div>
                <div className="bg-charcoal text-cream p-4 rounded-lg">
                  <h3 className="text-xl font-bold uppercase mb-2">The Taylor Suit - Youth</h3>
                  <p className="text-warmGray text-sm mb-3">Sized for youth. Same premium quality.</p>
                  <p className="text-2xl font-black text-warmSage">$250.00</p>
                </div>
              </div>

              {/* The Taylor Pants */}
              <div 
                className="group cursor-pointer"
                onClick={() => window.location.href = '/products/the-taylor-pants'}
              >
                <div className="relative overflow-hidden aspect-[4/5] border-4 border-charcoal rounded-lg mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/ygs0x6wd_IMG_2794.JPG" 
                    alt="The Taylor Pants"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/20 transition-all duration-300 flex items-center justify-center">
                    <Button className="opacity-0 group-hover:opacity-100 transition-opacity bg-warmSage text-charcoal hover:bg-warmSage/90 font-bold">
                      VIEW PRODUCT
                    </Button>
                  </div>
                </div>
                <div className="bg-charcoal text-cream p-4 rounded-lg">
                  <h3 className="text-xl font-bold uppercase mb-2">The Taylor Pants</h3>
                  <p className="text-warmGray text-sm mb-3">Complete the look with matching pants.</p>
                  <p className="text-2xl font-black text-warmSage">$125.00</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ContactButton />

      {/* What's Hook It Up? Info Dialog */}
      <Dialog open={hookItUpInfoOpen} onOpenChange={setHookItUpInfoOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-charcoal">What's "Hook It Up"?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-charcoal">
              <strong>"Hook It Up"</strong> is our way of making premium gear more accessible. See something you love but the price isn't quite right? Make us an offer!
            </p>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-bold text-charcoal mb-2">How It Works:</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-charcoal">
                <li>Submit your offer with your contact info</li>
                <li>We'll review it personally (usually within 24 hours)</li>
                <li>If accepted, you'll get a one-time discount code</li>
                <li>Complete your purchase at your offered price</li>
              </ol>
            </div>
            
            <div className="bg-warmSage/20 p-4 rounded-lg">
              <h3 className="font-bold text-charcoal mb-2">Our Promise:</h3>
              <p className="text-sm text-charcoal">
                Every offer gets a real human review. We're here to build relationships, not just make sales. Built on perseverance, built for you.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cart Modal */}
      <CartModal 
        isOpen={cartModalOpen}
        onClose={() => setCartModalOpen(false)}
        cart={cart}
        goToCheckout={goToCheckout}
      />

      {/* Virtual Try-On Modal */}
      <VirtualTryOn 
        product={product}
        isOpen={virtualTryOnOpen}
        onClose={() => setVirtualTryOnOpen(false)}
      />
    </div>
  );
};

export default ProductDetail;
